import { ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useScrollLock } from "@/lib/scrollLock";

interface Props {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Sticky footer (kept outside the scrollable content). */
  footer?: ReactNode;
  /** Sticky header (above the scrollable content). */
  header?: ReactNode;
  ariaLabel?: string;
  /** Override the panel width. Defaults to "w-full max-w-md". */
  widthClass?: string;
}

const PANEL_BASE =
  "fixed top-0 right-0 bottom-0 z-50 bg-bg shadow-warm-lg flex flex-col will-change-transform";

const SCRIM_BASE =
  "fixed inset-0 z-40 bg-ink/30 backdrop-blur-md transition-opacity duration-[240ms] ease-out";

/**
 * Right-anchored sidebar with React portal — same pattern as the tablet's
 * src/components/Drawer.js. Slide-from-right via translateX, locked body
 * scroll while open, ESC and scrim click to close.
 */
export const RightDrawer = ({
  open,
  onClose,
  children,
  footer,
  header,
  ariaLabel = "Tiroir",
  widthClass = "w-full max-w-md",
}: Props): JSX.Element | null => {
  const closeRef = useRef<HTMLButtonElement>(null);

  // iOS-safe body scroll lock (preserves scrollY)
  useScrollLock(open);

  // ESC closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus the close button on open for keyboard users
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => closeRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={`${SCRIM_BASE} ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`${PANEL_BASE} ${widthClass}`}
        style={{
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 280ms cubic-bezier(.4, 0, .2, 1)",
        }}
      >
        {/* Default close button — only when no custom header is provided. */}
        {!header && (
          <div className="flex justify-end p-3 pt-safe">
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-surface shadow-warm-sm flex items-center justify-center text-ink"
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
        )}
        {header}

        <div className="flex-1 overflow-y-auto overscroll-contain">{children}</div>

        {footer && (
          <div
            className="border-t border-border px-5 pt-4 bg-surface"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 32px)" }}
          >
            {footer}
          </div>
        )}
      </aside>
    </>,
    document.body
  );
};
